import type { CSSProperties } from 'react'

/**
 * Shared ticker logo tile used across the movers boards (and reusable on the screener / stock pages).
 *
 * WHY IT EXISTS. /api/logo returns a real image for most symbols, but two common cases render as an
 * empty-looking white square that reads like an unchecked checkbox:
 *   1. a WIDE WORDMARK (measured: RPGL is 1920x366, transparent) that object-contain shrinks to an
 *      invisible sliver inside a square tile, and
 *   2. a fully TRANSPARENT / near-blank icon, common for SPACs, warrants and tiny small-caps.
 * This tile sits a ticker MONOGRAM behind the image, so those cases show clean initials instead of an
 * empty box; a real OPAQUE logo (NVDA, SDEV, ...) simply covers the monogram. Pure markup, no JS, so
 * it stays a server component and fully crawlable, and it is identical on the /shot capture surface.
 */
export default function TickerLogo({
  symbol,
  size = 32,
  rounded = 'rounded-lg',
  eager = false,
  className = '',
}: {
  symbol: string
  size?: number
  /** Tailwind rounding class for the tile; match the surrounding design. */
  rounded?: string
  eager?: boolean
  /** Extra classes for the tile wrapper (e.g. layout/margin classes from a replaced inline tile). */
  className?: string
}) {
  const initials = (symbol.replace(/[^A-Za-z0-9]/g, '').slice(0, 2) || '?').toUpperCase()
  const inner = size - 2
  const tile: CSSProperties = { width: size, height: size }
  const imgStyle: CSSProperties = { width: inner, height: inner }
  return (
    <span
      className={`relative flex shrink-0 items-center justify-center overflow-hidden ${rounded} bg-white ring-1 ring-black/5 ${className}`}
      style={tile}
    >
      {/* Fallback monogram, BEHIND the image. Shows through for transparent/blank logos; hidden by a
          real opaque logo. */}
      <span
        aria-hidden
        className="absolute inset-0 flex items-center justify-center font-bold tracking-tight text-zinc-500"
        style={{ fontSize: Math.round(size * 0.4) }}
      >
        {initials}
      </span>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={`/api/logo?symbol=${encodeURIComponent(symbol)}`}
        alt=""
        width={inner}
        height={inner}
        className="relative object-contain"
        style={imgStyle}
        loading={eager ? 'eager' : 'lazy'}
      />
    </span>
  )
}
